<template>
  <v-container fluid>
    <el-overlay-loading :loading="pageloading" />
    <h3 v-if="showTitle" class="mb-5">Hipotekos / Įkeitimai</h3>

    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="filterItemList"
      :item-class="rowClass"
      :search="search"
      :hide-default-footer="hideFooter"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: 'Rodyti po:',
        itemsPerPageAllText: 'visi',
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :items-per-page="pagination.pageSize"
      :page.sync="pagination.page"
    >
      <template #top>
        <v-toolbar flat
          ><v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label=""
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
      </template>
      <template #[`item.pranesimo_busena`]="{ item }">
        <el-hipoteka-busena :busena="item.pranesimo_busena" />
      </template>
      <template #[`item.actions`]="{ item }">
        <el-table-action
          :item="item"
          @editItem="editItem(item)"
          @viewItem="editItem(item)"
          @deleteItem="deleteItem(item)"
          @restoreItem="restoreItem(item)"
          @duplicateItem="duplicateItem(item)"
          @transferItem="transferItem(item)"
          @priskirtiItem="startPriskyrimas(item)"
          @cancelPriskirtiItem="cancelPriskyrimas(item)"
        />
        <v-tooltip top v-if="item.obj_vykdo_snau_id">
          <template v-slot:activator="{ on, attrs }">
            <v-chip small outlined class="mt-1 mb-1" v-bind="attrs" v-on="on">
              <div class="tikrina-chip text-truncate">
                Priskirta {{ item.obj_vykdo }}
              </div>
            </v-chip>
          </template>
          <div class="tooltip-text">
            Priskirta<br />
            {{ item.obj_vykdo }}
          </div>
        </v-tooltip>
      </template>
      <template slot="loading">
        Kraunasi...
      </template>
      <template slot="no-results">
        Duomenų nėra
      </template>
      <template slot="no-data">
        Duomenų nėra
      </template>
      <template #[`footer.page-text`]="props">
        Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
        {{ props.itemsLength }}
      </template>
    </v-data-table>
    <div class="mt-4 mb-2">
      Rengiami, neperduoti registrui dokumentai bus pašalinti iš sistemos
      praėjus {{ salinimoLaikas }} k.d. nuo paskutinės pakeitimo datos.
    </div>
    <v-footer class="form-footer">
      <v-spacer></v-spacer>
      <el-button
        primary
        @click.native="
          printData('pranesimas/rengiami/print', 'Rengiami dokumentai')
        "
        :disabled="canPrint"
        :loading="loading"
        >Spausdinti</el-button
      >
    </v-footer>

    <modal-delete
      title="Šalinti dokumentą?"
      :text="
        `Ar norite pašalinti rengiamą dokumentą <strong>${modalDeleteVardas}</strong>?`
      "
      v-model="showDelete"
      :item="modalDeleteItem"
      @confirmDelete="confirmDelete"
    />
    <modal-confirm-priskyrimas
      title="Dokumento priskyrimas"
      :text="confirmPriskyrimasText"
      :item="confirmPriskyrimasItem"
      action="Priskyrimas"
      v-model="showConfirmPriskyrimas"
      @confirmPriskyrimas="confirmPriskyrimas"
      @cancelConfirm="cancelConfirmPriskyrimas"
    />
    <modal-confirm
      :title="modalConfirmTitle"
      :text="modalConfirmText"
      :action="modalConfirmAction"
      v-model="showConfirm"
      :item="modalConfirmItem"
      @confirmDuplicate="confirmDuplicate"
      @cancelConfirm="closeConfirm"
    />
    <modal-perdavimas
      v-model="showPerdavimas"
      :item="modalPerdavimasItem"
      @confirmPerdavimas="confirmPerdavimas"
      @cancelPerdavimas="cancelPerdavimas"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import HipotekaMap from '../hipoteka/hipotekaMap'
import ModalDelete from '../modals/ModalDelete'
import ModalConfirmPriskyrimas from '../modals/ModalConfirm'
import ModalConfirm from '../modals/ModalConfirm'
import ModalPerdavimas from '../modals/ModalPerdavimas'
import print from '../../mixins/print'
import userRole from '../../mixins/userRole'
import kurtiPagrindu from './kurtiPagrindu'

export default {
  name: 'PranesimasRengiami',
  components: {
    ModalDelete,
    ModalConfirm,
    ModalConfirmPriskyrimas,
    ModalPerdavimas,
  },
  mixins: [userRole, print, kurtiPagrindu],
  props: {
    singleTab: Boolean,
  },
  data() {
    return {
      pageloading: true,
      loading: true,
      search: null,
      showDelete: false,
      modalDeleteVardas: '',
      modalDeleteItem: null,
      showPerdavimas: false,
      modalPerdavimasItem: null,
      pranesimai: [],
      searchData: {},
      headers: [
        {
          text: 'Dokumento numeris',
          align: 'start',
          value: 'dokumento_numeris',
        },
        {
          text: 'Pranešimo rūšis',
          align: 'start',
          value: 'hipotekaTipas',
        },
        {
          text: 'Duomenų teikėjas',
          align: 'start',
          value: 'duomenu_teikejas',
        },
        {
          text: 'Šalis (pirma)',
          align: 'start',
          sortable: false,
          value: 'skolininkai',
        },
        {
          text: 'Šalis (antra)',
          align: 'start',
          sortable: false,
          value: 'kreditoriai',
        },
        {
          text: 'Būsena',
          align: 'start',
          value: 'pranesimo_busena',
        },
        {
          text: 'Paskutinis pakeitimas',
          align: 'start',
          value: 'atnaujinimo_data',
        },
        {
          text: 'Paskutinis veiksmus atlikęs asmuo',
          align: 'start',
          value: 'parenges_asmuo',
        },
        {
          text: 'Numatoma pašalinimo data',
          align: 'start',
          value: 'numatoma_pasalinimo_data',
        },
        {
          text: 'Veiksmai',
          align: 'center',
          sortable: false,
          value: 'actions',
        },
      ],
      itemsPerPageOptions: [10, 50, 100, -1],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
        sort: null,
        sortDesc: [],
        direction: 'asc',
      },
      lockInterval: null,
      lockTimeout: 10000,
      confirmPriskyrimasText: null,
      confirmPriskyrimasItem: null,
      showConfirmPriskyrimas: false,
      salinimoLaikas: 30,
    }
  },
  computed: {
    ...mapGetters(['userID', 'listInfo']),

    filterItemList() {
      return this.pranesimai.map(pranesimas => {
        pranesimas.hipotekaTipas = HipotekaMap['hipotekaTipas'][
          pranesimas.pranesimo_rusis
        ]
          ? HipotekaMap['hipotekaTipas'][pranesimas.pranesimo_rusis].title
          : pranesimas.pranesimo_rusis
        pranesimas.skolininkai = pranesimas.skol_ir_gav.join(', ')
        pranesimas.kreditoriai = pranesimas.kred_ir_dav.join(', ')

        pranesimas.canDuplicate = pranesimas.galima_kurti_pagrindu
        pranesimas.canTransfer =
          this.roleOr([
            'DT_notaras',
            'DT_notaro_padejejas',
            'DT_notaro_atstovas',
          ]) &&
          (pranesimas.pranesimo_busena === 'rengiamas' ||
            pranesimas.pranesimo_busena === 'grazintas')

        if (pranesimas.obj_uzrakinta) {
          pranesimas.canLock = true
          pranesimas.lockMsg = pranesimas.obj_uzrakines_asmuo
          pranesimas.canTransfer = false
        } else {
          pranesimas.canEdit = true
          pranesimas.canDelete = pranesimas.pranesimo_busena !== 'grazintas'
        }

        if (pranesimas.zug_pasirasymas_vadovas) {
          if (
            (this.roleDT && pranesimas.zug_busena_tolimesne === 'perduotas') ||
            (this.roleRGV && pranesimas.zug_busena_tolimesne === 'atmestas')
          ) {
            pranesimas.showSign = true
          }
          if (
            pranesimas.pranesimo_busena === 'rengiamas' &&
            !this.roleOr(['DT_notaras', 'DT_notaro_atstovas']) &&
            !pranesimas.obj_uzrakinta
          ) {
            pranesimas.canView = true
            pranesimas.canEdit = false
            pranesimas.canDelete = false
            pranesimas.canTransfer = false
          }
        }

        pranesimas.canPriskirti = false
        pranesimas.canCancelPriskirti = false
        if (
          this.isJuridinis &&
          pranesimas.pranesimo_busena === 'laukia_patvirtinimo'
        ) {
          const priskirtaSau = pranesimas.obj_vykdo_snau_id === this.userID
          pranesimas.canPriskirti = !priskirtaSau
          pranesimas.canCancelPriskirti = priskirtaSau
          pranesimas.canDelete = false
        }

        return pranesimas
      })
    },
    hideFooter() {
      return this.pagination.total <= 10
    },
    canPrint() {
      return this.filterItemList.length === 0
    },
    isLocked() {
      return this.pranesimai.filter(item => item.obj_uzrakinta).length > 0
    },
    showTitle() {
      return !this.singleTab
    },
    isJuridinis() {
      return this.roleOr(['DT_JA'])
    },
  },
  beforeDestroy() {
    this.stopLoading()
  },
  created() {
    this.pageloading = true
    this.loading = true
    this.getData()
  },
  methods: {
    getData() {
      this.$axios
        .post(`pranesimas/rengiami`)
        .then(res => {
          this.pranesimai = res.data.pranesimai
          this.pagination.total = this.pranesimai.length
          this.salinimoLaikas = res.data.salinimo_laikas

          if (this.$route.params.toList) {
            this.pagination = this.listInfo.pagination
            this.search = this.listInfo.search
            this.$route.params.toList = false
          }

          this.checkLoading()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
        })
    },
    checkLoading() {
      if (this.isLocked) {
        if (!this.lockInterval) {
          this.lockInterval = setInterval(() => {
            this.getData()
          }, this.lockTimeout)
        }
      } else {
        this.stopLoading()
      }
    },
    stopLoading() {
      clearInterval(this.lockInterval)
    },
    editItem(item) {
      const objektasID = item.obj_id
      this.$store.dispatch('objektasID', objektasID)
      this.$store.dispatch('hipotekaTipas', item.pranesimo_rusis)
      // this.$store.dispatch('objektasVeiksmas', item.obj_veiksmas ?? 0)

      const listInfo = {
        obj_id: item.obj_id,
        routeName: 'rengiamiDokumentaiHipoteka',
        pagination: this.pagination,
        search: this.search,
      }

      let routeName = 'hipotekaPranesimas'
      if (
        item.pranesimo_busena === 'laukia_patvirtinimo' &&
        this.roleOr(['DT_JA'])
      ) {
        routeName = 'hipotekaRegistravimas'
      }

      this.$store.dispatch('listInfo', listInfo).then(
        this.$router.push({
          name: routeName,
          params: {
            objektasID: objektasID,
          },
        })
      )
    },
    deleteItem(item) {
      this.modalDeleteVardas = item.dokumento_numeris ?? ''
      this.modalDeleteItem = Object.assign({}, item)
      this.showDelete = true
    },
    confirmDelete(item) {
      this.loading = true

      this.$axios
        .delete(`objektas/${item.obj_id}`)
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.getData()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    transferItem(item) {
      this.modalPerdavimasItem = item
      this.showPerdavimas = true
    },
    confirmPerdavimas(data) {
      this.$axios
        .post(`perdavimas/perduoti`, data)
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.getData()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.showPerdavimas = false
        })
    },
    cancelPerdavimas() {
      this.showPerdavimas = false
    },
    startPriskyrimas(item) {
      this.priskirtiItem(item, true)
    },
    cancelPriskyrimas(item) {
      this.priskirtiItem(item, false)
    },
    priskirtiItem(item, priskirti, force = false) {
      const data = {
        obj_id: item.obj_id,
        priskirti: priskirti,
        force: force,
      }

      this.$axios
        .post(`objektas/${item.obj_id}/priskirti`, data)
        .then(res => {
          const priskyrimas = res.data.priskyrimas

          if (priskyrimas.success) {
            window._VMA.$emit('DATA_OK', 'Duomenys išsaugoti')
            this.getData()
            return
          }

          item.obj_vykdo = priskyrimas.obj_vykdo
          item.obj_vykdo_snau_id = priskyrimas.obj_vykdo_snau_id
          this.openConfirmPriskyrimas(item)
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          //
        })
    },
    openConfirmPriskyrimas(item) {
      this.confirmPriskyrimasText = `Dokumentas jau yra priskirtas <b>${item.obj_vykdo}</b>. <br />Ar tikrai norite dokumentą priskirti sau?`
      this.confirmPriskyrimasItem = item
      this.showConfirmPriskyrimas = true
    },
    confirmPriskyrimas(item) {
      this.priskirtiItem(item, true, true)
    },
    cancelConfirmPriskyrimas() {
      this.showConfirm = false
    },
    rowClass(item) {
      if (item.pasalinimo_status === 'warning') {
        return `zurnalas-warning`
      }
      if (item.pasalinimo_status === 'outdate') {
        return `zurnalas-outdate`
      }
      if (item.pasalinimo_status === 'repost') {
        return `zurnalas-repost`
      }
      return ''
    },
  },
}
</script>
<style lang="scss" scoped>
.potipis {
  background: #8eb693;

  .v-card__text {
    &.active {
      background: #2c7837;
      color: #fff;
    }
  }
}
</style>
